import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { OpenFeature } from '@openfeature/angular-sdk';
import { Observable, of, shareReplay, Subject, tap, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  ILoginApiResponse,
  ILoginSubmit,
  ILoginUserDetailsApiResponse,
  IUserInfo,
} from '../models/login/login.interface';
import { JwtHelperService } from './jwt-helper.service';
import { TURNSTILE_ACTION } from './turnstile/constant/turnstile.constant';
import { TurnstileService } from './turnstile/turnstile.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private url = environment.backend_portal_url;
  private http = inject(HttpClient);
  private jwtHelper = inject(JwtHelperService);
  private injector = inject(Injector);
  private turnstileService = inject(TurnstileService);
  private userLoggedInSubject = new Subject<string>();
  userLoggedIn$ = this.userLoggedInSubject.asObservable();

  refreshUserData() {
    const load_url = this.url + 'api/v1/user-detail';
    return this.http
      .get<ILoginUserDetailsApiResponse>(load_url, {
        params: new HttpParams().set('email', this.getUserInfo()?.email ?? ''),
      })
      .pipe(tap(data => this.setUserInfo(data.data as IUserInfo)));
  }

  getIpInfo(): Observable<any> {
    return this.http.get('https://ipinfo.io/json');
  }

  login(loginForm: ILoginSubmit, fingerprint: string): Observable<ILoginApiResponse> {
    const load_url = this.url + 'api/v1/auth/login';
    return this.turnstileService.getHeaders(TURNSTILE_ACTION.LOGIN).pipe(
      switchMap(turnstileHeaders => {
        const headers = new HttpHeaders({
          'X-Device-Identifier': fingerprint,
          ...turnstileHeaders,
        });
        return this.http.post<ILoginApiResponse>(load_url, loginForm, { headers });
      })
    );
  }

  refreshToken() {
    const load_url = this.url + 'api/v1/auth/refresh';
    const fingerprint = this.getFingerPrintId(this.getUserInfo()?.email || '');
    const headers = new HttpHeaders({
      'X-Device-Identifier': fingerprint ?? '',
    });
    return this.http
      .post(
        load_url,
        {
          email: this.getUserInfo()?.email,
          refreshToken: this.getRefreshToken(),
        },
        { headers }
      )
      .pipe(shareReplay(1));
  }

  changePassword(obj: any) {
    const load_url = this.url + 'api/v1/change-password';
    return this.http.patch(load_url, obj);
  }

  forgotPassword(password: string) {
    const load_url = this.url + 'api/v1/auth/reset-password';

    return this.http.patch(
      load_url,
      { password },
      {
        headers: {
          Authorization: `Bearer ${this.getTempToken()}`,
        },
      }
    );
  }

  logout(isReload = true) {
    const getEmail = this.getUserInfo()?.email;
    const currentFingerprint = getEmail + 'deviceFingerprint';
    const dataToKeep: Record<string, string> = {};

    // Store all data we want to preserve
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      // Preserve device fingerprints
      if (key && key.includes('deviceFingerprint')) {
        dataToKeep[key] = localStorage.getItem(key)!;
      }

      // Preserve coming soon banner seen state
      if (key === 'comingSoonBannerSeen_v2') {
        dataToKeep[key] = localStorage.getItem(key)!;
      }

      if (key && key.includes('dashboard_tour_finished_')) {
        dataToKeep[key] = localStorage.getItem(key)!;
      }
    }

    const isRemember = localStorage.getItem('isRemember') === 'true';

    localStorage.clear();

    // Restore all preserved data
    Object.keys(dataToKeep).forEach(key => {
      localStorage.setItem(key, dataToKeep[key]);
    });

    if (isRemember && dataToKeep[currentFingerprint]) {
      localStorage.setItem(currentFingerprint, dataToKeep[currentFingerprint]);
    } else if (!isRemember) {
      localStorage.removeItem(currentFingerprint);
    }

    if (isReload) {
      window.location.reload();
    }
  }

  setUserInfo(user_info: IUserInfo) {
    localStorage.setItem('user_info', JSON.stringify(user_info));

    // Set OpenFeature context
    const ctx = {
      targetingKey: user_info.merchantId,
      userID: user_info.uuid,
      merchantID: user_info.merchantId,
    };
    OpenFeature.setContext(ctx);
    this.userLoggedInSubject.next(user_info?.uuid);
    this.initializeGTMContext();
  }

  private initializeGTMContext(): void {
    import('./google-tag-manager').then(({ GTMServiceFactory }) => {
      const gtmService = this.injector.get(GTMServiceFactory);
      gtmService.initializeFromUserInfo();
    });
  }

  getUserInfo() {
    const userData = localStorage.getItem('user_info');
    if (userData !== null) {
      return JSON.parse(userData) as IUserInfo;
    }
    return null;
  }

  initializeOpenFeatureContext(): any {
    const userInfo = this.getUserInfo();
    if (userInfo) {
      const ctx = {
        targetingKey: userInfo.merchantId,
        userID: userInfo.uuid,
        merchant_id: userInfo.merchantId,
      };
      return ctx;
    }
  }

  checkPassword(data: { password: string }) {
    return this.http.post(this.url + 'api/v1/users/check-password', data);
  }

  setEmail(email: string) {
    localStorage.setItem('email', email);
  }

  getEmail() {
    const email = localStorage.getItem('email');
    if (email !== null) {
      return email;
    }
    return '';
  }

  setAccessToken(accessToken: string) {
    localStorage.setItem('accessToken', accessToken);

    // Store the token expiration time
    const expirationTime = this.jwtHelper.getTokenExpirationDate(accessToken);
    if (expirationTime) {
      localStorage.setItem('accessTokenExpiration', expirationTime.toString());
    }
  }

  setTempToken(token: string) {
    localStorage.setItem('token', token);
  }

  getTempToken() {
    return localStorage.getItem('token');
  }

  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  /**
   * Checks if the access token is expired
   * @param offsetSeconds Buffer time in seconds before actual expiration (default: 60)
   * @returns true if token is expired or will expire within the offset time
   */
  isAccessTokenExpired(offsetSeconds: number = 10): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    return this.jwtHelper.isTokenExpired(token, offsetSeconds);
  }

  /**
   * Checks if the refresh token is expired
   * @param offsetSeconds Buffer time in seconds before actual expiration (default: 60)
   * @returns true if token is expired or will expire within the offset time
   */
  isRefreshTokenExpired(offsetSeconds: number = 10): boolean {
    const token = this.getRefreshToken();
    if (!token) return true;

    return this.jwtHelper.isTokenExpired(token, offsetSeconds);
  }

  /**
   * Gets a valid access token, refreshing if needed
   * @returns Observable of the access token
   */
  getValidAccessToken(): Observable<string> {
    // If the access token is not expired, return it
    if (!this.isAccessTokenExpired()) {
      return of(this.getAccessToken() || '');
    }

    // If the refresh token is expired, user needs to login again
    if (this.isRefreshTokenExpired()) {
      this.logout(false);
      return throwError(() => new Error('Session expired. Please login again.'));
    }

    // Otherwise, try to refresh the token
    return this.refreshToken().pipe(
      switchMap((res: any) => {
        const { accessToken } = res.data;
        return of(accessToken);
      })
    );
  }

  setRefreshToken(refreshToken: string) {
    localStorage.setItem('refreshToken', refreshToken);

    // Store the refresh token expiration time
    const expirationTime = this.jwtHelper.getTokenExpirationDate(refreshToken);
    if (expirationTime) {
      localStorage.setItem('refreshTokenExpiration', expirationTime.toString());
    }
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  isLoggedIn() {
    return this.getAccessToken() != null;
  }

  getFingerPrintId(email: string) {
    const storedFingerprint = localStorage.getItem(email + 'deviceFingerprint');
    return storedFingerprint;
  }
  setFingerPrintId(email: string, fingerPrintId: string) {
    localStorage.setItem(email + 'deviceFingerprint', fingerPrintId);
  }

  getIsRemember() {
    const isRemember = localStorage.getItem('isRemember');
    return isRemember;
  }
  setIsRemember() {
    localStorage.setItem('isRemember', 'true');
  }

  enrollTOTP(qrCodeLevel: string = 'Medium', qrCodeSize: number = 256): Observable<any> {
    const load_url = this.url + 'api/v1/users/mfa/totp/enroll';

    // Use access token if available (for authenticated users), otherwise use temp token (during login flow)
    const token = this.getAccessToken() || this.getTempToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.post(
      load_url,
      {
        qrCodeLevel,
        qrCodeSize,
      },
      { headers }
    );
  }

  confirmTOTP(otp: string): Observable<any> {
    const load_url = this.url + 'api/v1/users/mfa/totp/confirm';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.getAccessToken()}`,
    });
    return this.http.post(
      load_url,
      {
        otp,
      },
      { headers }
    );
  }

  /**
   * Shows the session ended dialog with a custom message
   * Uses lazy loading to avoid circular dependency with DialogComponentService
   * @param reason The reason for session termination ('role-changed' | 'session-terminated' | 'device-changed')
   */
  showSessionEndedDialog() {
    let title = 'Session Ended';
    let description = 'Your user role has changed. For security reasons, please log in again.';

    // Use lazy import to break circular dependency
    // some dialog component already import authService
    import('../components/dialog/dialog.component.service').then(({ DialogComponentService }) => {
      const dialogService = this.injector.get(DialogComponentService);
      dialogService.openSessionEndedDialog({
        title,
        description,
      });
    });
  }
}
